import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

const MOBILE_MQ = '(max-width: 749px)';

/**
 * Product list header controls — dual-collection tabs, metafield carousel nav (‹ 1/N ›),
 * and mobile grid group filtering when `grid_on_mobile` is on.
 *
 * @extends {Component}
 */
class DevProductList extends Component {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MediaQueryList | null} */
  #mobileMql = null;

  /** @type {HTMLElement | null} */
  #scroller = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleTabKeydown);

    const slideshow = this.#getSlideshow();
    slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);

    this.#mobileMql = window.matchMedia(MOBILE_MQ);
    this.#mobileMql.addEventListener('change', this.#handleViewportChange);

    this.#bindScrollerObservers();
    this.#syncInitialTab();
    this.#updateCarouselNav();
    this.#syncLayoutInert();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleTabKeydown);

    const slideshow = this.#getSlideshow();
    slideshow?.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    this.#unbindScrollerObservers();

    this.#mobileMql?.removeEventListener('change', this.#handleViewportChange);
    this.#mobileMql = null;
  }

  #unbindScrollerObservers() {
    if (this.#scroller instanceof HTMLElement) {
      this.#scroller.removeEventListener('scroll', this.#handleScrollerScroll);
    }

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#scroller = null;
  }

  #bindScrollerObservers() {
    const bind = () => {
      const scroller = this.#getSlideshow()?.refs?.scroller;
      if (!(scroller instanceof HTMLElement)) return false;

      if (this.#scroller === scroller) return true;

      this.#unbindScrollerObservers();
      this.#scroller = scroller;
      this.#scroller.addEventListener('scroll', this.#handleScrollerScroll, { passive: true });

      this.#resizeObserver = new ResizeObserver(() => this.#updateCarouselNav());
      this.#resizeObserver.observe(this.#scroller);
      this.#updateCarouselNav();
      return true;
    };

    if (!bind()) {
      requestAnimationFrame(() => {
        if (!bind()) requestAnimationFrame(bind);
      });
    }
  }

  #handleScrollerScroll = () => {
    this.#updateCarouselNav();
  };

  /**
   * Keep the CSS-hidden layout branch out of focus / hit-testing without a second card pipeline.
   */
  #syncLayoutInert() {
    const section = this.#getSection();
    if (!section) return;

    const mobileGrid = this.#getMobileGrid();
    if (!mobileGrid) return;

    const desktop = section.querySelector('[data-product-list-desktop]');
    const isMobile = this.#mobileMql?.matches ?? window.matchMedia(MOBILE_MQ).matches;

    if (desktop instanceof HTMLElement) {
      desktop.toggleAttribute('inert', isMobile);
    }

    mobileGrid.toggleAttribute('inert', !isMobile);
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getSection() {
    const sectionId = this.dataset.sectionId;

    if (sectionId) {
      const byId = document.getElementById(`shopify-section-${sectionId}`);
      if (byId instanceof HTMLElement) return byId.querySelector('[data-testid="product-list"]');
    }

    return this.closest('[data-testid="product-list"]');
  }

  /**
   * @returns {import('slideshow').Slideshow | null}
   */
  #getSlideshow() {
    const section = this.#getSection();
    if (!section) return null;

    const carouselRef = this.dataset.carouselRef;
    if (carouselRef) {
      const byRef = section.querySelector(`slideshow-component[ref="${carouselRef}"]`);
      if (byRef) return /** @type {import('slideshow').Slideshow} */ (byRef);
    }

    return section.querySelector('slideshow-component');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getCarouselNav() {
    const section = this.#getSection();
    return section?.querySelector('[data-product-list-carousel-nav]') ?? null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getMobileGrid() {
    const section = this.#getSection();
    return section?.querySelector('[data-product-list-mobile-grid]') ?? null;
  }

  /**
   * @returns {boolean}
   */
  #isMobileGridView() {
    const mobileGrid = this.#getMobileGrid();
    if (!mobileGrid) return false;
    return this.#mobileMql?.matches ?? window.matchMedia(MOBILE_MQ).matches;
  }

  #syncInitialTab() {
    const sync = () => {
      if (this.#isMobileGridView()) {
        const activeTab = this.querySelector('[data-collection-index][aria-selected="true"]');
        const collectionIndex = parseInt(activeTab?.getAttribute('data-collection-index') || '0', 10);
        this.#setActiveCollection(Number.isNaN(collectionIndex) ? 0 : collectionIndex);
        return;
      }

      const slideshow = this.#getSlideshow();
      if (!slideshow) {
        this.#updateCarouselNav();
        return;
      }

      const initialIndex = slideshow.current ?? 0;
      this.#setActiveCollection(
        this.#getCollectionIndexFromSlide(slideshow.slides?.[initialIndex], initialIndex)
      );
    };

    sync();
    requestAnimationFrame(sync);
    requestAnimationFrame(() => requestAnimationFrame(sync));
  }

  #handleViewportChange = () => {
    this.#syncInitialTab();
    this.#updateCarouselNav();
    this.#syncLayoutInert();
  };

  /**
   * @returns {number[]}
   */
  #getSlideStarts() {
    return (this.dataset.slideStarts || '0')
      .split(',')
      .map((value) => parseInt(value, 10))
      .filter((value) => !Number.isNaN(value));
  }

  /**
   * @param {number} slideIndex
   * @returns {number}
   */
  #getCollectionIndex(slideIndex) {
    const starts = this.#getSlideStarts();
    let collectionIndex = 0;

    for (const [index, start] of starts.entries()) {
      if (slideIndex >= start) {
        collectionIndex = index;
      }
    }

    return collectionIndex;
  }

  /**
   * @param {Element | undefined | null} slide
   * @param {number} slideIndex
   * @returns {number}
   */
  #getCollectionIndexFromSlide(slide, slideIndex) {
    const groupElement =
      slide instanceof Element
        ? slide.querySelector('[data-collection-group]') ?? slide.closest('[data-collection-group]')
        : null;
    const group = groupElement?.getAttribute('data-collection-group');

    if (group != null) {
      const collectionIndex = parseInt(group, 10);
      if (!Number.isNaN(collectionIndex)) return collectionIndex;
    }

    return this.#getCollectionIndex(slideIndex);
  }

  /**
   * @param {number} slideStart
   * @param {number} collectionIndex
   * @param {Event} [event]
   */
  #goToCollection(slideStart, collectionIndex, event) {
    this.#setActiveCollection(collectionIndex);

    if (this.#isMobileGridView()) return;

    const slideshow = this.#getSlideshow();
    if (!slideshow) return;

    slideshow.select(slideStart, event);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const prev = event.target.closest('[data-product-list-nav-prev]');
    if (prev && this.#getSection()?.contains(prev)) {
      event.preventDefault();
      this.#getSlideshow()?.previous(event);
      return;
    }

    const next = event.target.closest('[data-product-list-nav-next]');
    if (next && this.#getSection()?.contains(next)) {
      event.preventDefault();
      this.#getSlideshow()?.next(event);
      return;
    }

    const tab = event.target.closest('button[data-collection-index]');
    if (!tab || !this.contains(tab)) return;

    event.preventDefault();

    const collectionIndex = parseInt(tab.getAttribute('data-collection-index') || '0', 10);
    const slideStart = parseInt(tab.getAttribute('data-slide-start') || '0', 10);

    this.#goToCollection(slideStart, collectionIndex, event);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleTabKeydown = (event) => {
    const tabs = Array.from(this.querySelectorAll('button[data-collection-index]')).filter(
      (tab) => tab instanceof HTMLButtonElement
    );

    if (tabs.length < 2) return;

    const currentIndex = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    tabs[nextIndex]?.click();
  };

  /**
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    const slideshow = this.#getSlideshow();
    if (!slideshow || event.target !== slideshow) return;

    const collectionIndex = this.#getCollectionIndexFromSlide(event.detail.slide, event.detail.index);
    this.#setActiveCollection(collectionIndex);
    this.#updateCarouselNav();
  };

  /**
   * Page counter math mirrors slideshow.js `#updatePageCounter` (counter-pages).
   */
  #updateCarouselNav() {
    const nav = this.#getCarouselNav();
    if (!nav) return;

    const slideshow = this.#getSlideshow();
    const currentEl = nav.querySelector('[data-product-list-nav-current]');
    const totalEl = nav.querySelector('[data-product-list-nav-total]');
    const prevBtn = nav.querySelector('[data-product-list-nav-prev]');
    const nextBtn = nav.querySelector('[data-product-list-nav-next]');

    if (!(currentEl instanceof HTMLElement) || !(totalEl instanceof HTMLElement)) return;

    if (!slideshow || this.#isMobileGridView()) return;

    const slides = slideshow.slides;
    const scroller = slideshow.refs?.scroller;

    if (!slides?.length || !(scroller instanceof HTMLElement)) {
      nav.dataset.singlePage = '';
      return;
    }

    const viewport = scroller.clientWidth;
    if (viewport === 0) return;

    const first = slides[0];
    if (!(first instanceof HTMLElement)) return;

    const second = slides[1];
    const base = first.offsetLeft;
    const pitch =
      second instanceof HTMLElement ? second.offsetLeft - base : first.offsetWidth;
    const perPage = pitch > 0 ? Math.max(1, Math.round(viewport / pitch)) : 1;
    const totalPages = Math.max(1, Math.ceil(slides.length / perPage));

    const scrollPos = scroller.scrollLeft;
    const maxScroll = Math.max(0, scroller.scrollWidth - viewport);

    let currentPage = 1;
    let closest = Infinity;

    for (let page = 0; page < totalPages; page++) {
      const anchor = slides[Math.min(page * perPage, slides.length - 1)];
      if (!(anchor instanceof HTMLElement)) continue;

      const target = Math.min(Math.max(0, anchor.offsetLeft - base), maxScroll);
      const distance = Math.abs(target - scrollPos);

      if (distance < closest) {
        closest = distance;
        currentPage = page + 1;
      }
    }

    currentEl.textContent = `${currentPage}`;
    totalEl.textContent = `${totalPages}`;

    if (totalPages <= 1) {
      nav.dataset.singlePage = '';
    } else {
      delete nav.dataset.singlePage;
    }

    if (prevBtn instanceof HTMLButtonElement) {
      prevBtn.disabled = currentPage <= 1;
    }

    if (nextBtn instanceof HTMLButtonElement) {
      nextBtn.disabled = currentPage >= totalPages;
    }
  }

  #syncMobileGrid() {
    const mobileGrid = this.#getMobileGrid();
    if (!mobileGrid) return;

    const activeTab = this.querySelector('[data-collection-index][aria-selected="true"]');
    const collectionIndex = parseInt(activeTab?.getAttribute('data-collection-index') || '0', 10);
    const activeGroup = Number.isNaN(collectionIndex) ? 0 : collectionIndex;

    const groupedItems = mobileGrid.querySelectorAll('[data-collection-group]');
    if (groupedItems.length === 0) return;

    for (const item of groupedItems) {
      if (!(item instanceof HTMLElement)) continue;
      const group = parseInt(item.getAttribute('data-collection-group') || '0', 10);
      item.hidden = group !== activeGroup;
    }
  }

  /**
   * @param {number} collectionIndex
   */
  #setActiveCollection(collectionIndex) {
    const tabs = this.querySelectorAll('[data-collection-index]');

    for (const tab of tabs) {
      if (!(tab instanceof HTMLElement)) continue;

      const isActive = parseInt(tab.getAttribute('data-collection-index') || '0', 10) === collectionIndex;
      tab.setAttribute('aria-selected', String(isActive));
    }

    this.#syncMobileGrid();

    const section = this.#getSection();
    const shopAllLink = section?.querySelector('[data-shop-all-link]');

    if (!(shopAllLink instanceof HTMLAnchorElement)) return;

    const urls = (this.dataset.collectionUrls || '').split('|');
    const nextUrl = urls[collectionIndex];

    if (nextUrl) {
      shopAllLink.href = nextUrl;
      shopAllLink.removeAttribute('aria-disabled');
    }

    const visibility = (this.dataset.shopAllVisible || '').split(',');
    shopAllLink.hidden = visibility[collectionIndex] !== 'true';
  }
}

if (!customElements.get('dev-product-list-component')) {
  customElements.define('dev-product-list-component', DevProductList);
}
